import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import TicketFinance from '../components/TicketFinance';
import { useToast, Modal, ConfirmModal, StatusBadge, InvoiceBadge, formatDate, warrantyStatus, STATUS_LABELS, INVOICE_STATUS_LABELS, useIsAdmin } from '../components/shared';

function PartRow({ part, onUpdate, onDelete, isAdmin, invoices, onInvoiceClick }) {
  const ws = warrantyStatus(part);
  // Find invoices that contain this part
  const linkedInvoices = invoices?.filter(inv => inv.linked_parts?.find(lp => lp.id === part.id)) || [];

  return (
    <tr>
      <td>
        <div className="fw-500">{part.name}</div>
        {linkedInvoices.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {linkedInvoices.map(inv => (
              <span key={inv.id}
                onClick={() => onInvoiceClick(inv)}
                style={{
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--primary-dim)', border: '1px solid rgba(45,156,219,0.2)',
                  borderRadius: 20, padding: '2px 8px', fontSize: 11, color: 'var(--primary)'
                }}>
                🧾 {inv.invoice_number || 'Invoice'}{inv.amount ? ` — ${inv.amount.toLocaleString()} EGP` : ''}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="mono text-sm">{part.quantity}</td>
      <td>
        {part.warranty_months > 0 ? (
          <span>
            {part.warranty_months}mo
            {ws && <span className={`ml-auto mono text-sm ${ws.cls}`} style={{ marginLeft: 8 }}>{ws.label}</span>}
          </span>
        ) : <span className="text-dim text-sm">—</span>}
      </td>
      <td>
        {isAdmin ? (
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={!!part.is_faulty}
              onChange={e => onUpdate(part.id, { is_faulty: e.target.checked })}
              style={{ accentColor: 'var(--red)' }} />
            {part.is_faulty ? <span className="text-red text-xs">⚠ Faulty</span> : <span className="text-dim text-xs">OK</span>}
          </label>
        ) : (
          part.is_faulty ? <span className="text-red text-xs">⚠ Faulty</span> : <span className="text-dim text-xs">OK</span>
        )}
      </td>
      <td className="text-sm text-muted">{part.notes || '—'}</td>
      <td>{isAdmin && <button className="btn btn-danger btn-sm" onClick={() => onDelete(part.id)}>Del</button>}</td>
    </tr>
  );
}

function InvoiceDetailModal({ inv, onClose }) {
  return (
    <Modal title={`Invoice ${inv.invoice_number || '#'}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="grid-2">
          <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</div>
            <div className="fw-600 text-primary" style={{ fontSize: 18 }}>{inv.amount ? `${inv.amount.toLocaleString()} EGP` : '—'}</div></div>
          <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
            <InvoiceBadge status={inv.status} /></div>
        </div>
        <div className="grid-2">
          <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued</div>
            <div className="text-sm">{formatDate(inv.issued_at)}</div></div>
          <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sent</div>
            <div className="text-sm">{formatDate(inv.sent_at)}</div></div>
        </div>
        {inv.paid_at && <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paid</div>
          <div className="text-sm">{formatDate(inv.paid_at)}</div></div>}
        {inv.linked_parts?.length > 0 && (
          <div>
            <div className="text-xs text-dim mb-6" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Parts Covered</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {inv.linked_parts.map(p => (
                <span key={p.id} style={{
                  background: 'var(--bg3)', border: '1px solid var(--border)',
                  borderRadius: 20, padding: '3px 10px', fontSize: 12
                }}>{p.name}</span>
              ))}
            </div>
          </div>
        )}
        {inv.notes && <div><div className="text-xs text-dim mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
          <div className="text-sm text-muted">{inv.notes}</div></div>}
      </div>
    </Modal>
  );
}

function AddPartModal({ ticketId, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', quantity: 1, warranty_months: 0, notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit() {
    if (!form.name) return toast('Name required', 'error');
    setLoading(true);
    try {
      await api.createPart({ ticket_id: ticketId, ...form, quantity: Number(form.quantity), warranty_months: Number(form.warranty_months) });
      toast('Part added');
      onSave();
      onClose();
    } catch (e) {
      toast(e.message, 'error');
    } finally { setLoading(false); }
  }

  return (
    <Modal title="Add Part" onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>Add</button>
      </>
    }>
      <div className="field"><label>Part Name *</label>
        <input className="input" value={form.name} onChange={set('name')} placeholder="e.g. Capacitor 470μF" /></div>
      <div className="grid-2">
        <div className="field"><label>Quantity</label>
          <input className="input" type="number" min="1" value={form.quantity} onChange={set('quantity')} /></div>
        <div className="field"><label>Warranty (months)</label>
          <input className="input" type="number" min="0" value={form.warranty_months} onChange={set('warranty_months')} /></div>
      </div>
      <div className="field"><label>Notes</label>
        <input className="input" value={form.notes} onChange={set('notes')} /></div>
    </Modal>
  );
}

function AddInvoiceModal({ ticketId, onSave, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ invoice_number: '', amount: '', notes: '', issued_at: today });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setLoading(true);
    try {
      await api.createInvoice({ ticket_id: ticketId, ...form, amount: form.amount ? Number(form.amount) : null, issued_at: form.issued_at ? form.issued_at + 'T00:00:00.000Z' : null });
      toast('Invoice tracked');
      onSave();
      onClose();
    } catch (e) {
      toast(e.message, 'error');
    } finally { setLoading(false); }
  }

  return (
    <Modal title="Track Invoice" onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>Save</button>
      </>
    }>
      <div className="grid-2">
        <div className="field"><label>Invoice Number</label>
          <input className="input" value={form.invoice_number} onChange={set('invoice_number')} placeholder="INV-001" /></div>
        <div className="field"><label>Amount (EGP)</label>
          <input className="input" type="number" value={form.amount} onChange={set('amount')} /></div>
      </div>
      <div className="field"><label>Issued Date</label>
        <input type="date" className="input" value={form.issued_at} onChange={set('issued_at')} /></div>
      <div className="field"><label>Notes</label>
        <input className="input" value={form.notes} onChange={set('notes')} /></div>
    </Modal>
  );
}

function ReturnModal({ ticket, onClose }) {
  const [form, setForm] = useState({ fault_description: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  async function submit() {
    if (!form.fault_description) return toast('Fault description required', 'error');
    setLoading(true);
    try {
      const t = await api.createTicket({
        device_id: ticket.device_id,
        parent_ticket_id: ticket.id,
        fault_description: form.fault_description,
        notes: form.notes,
      });
      toast('Return ticket created');
      navigate(`/tickets/${t.id}`);
    } catch(e) {
      toast(e.message, 'error');
      setLoading(false);
    }
  }

  return (
    <Modal title="↩ Create Return Ticket" onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff' }} onClick={submit} disabled={loading}>
          {loading ? 'Creating...' : 'Create Return Ticket'}
        </button>
      </>
    }>
      <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--red-dim)', borderRadius: 6, fontSize: 13, color: 'var(--red)' }}>
        Device: <strong>{ticket.device_name}</strong> — Serial: <strong>{ticket.device_serial}</strong>
      </div>
      <div className="field">
        <label>New Fault Description *</label>
        <input className="input" value={form.fault_description}
          onChange={e => setForm(f => ({ ...f, fault_description: e.target.value }))}
          placeholder="What's the new issue?" autoFocus />
      </div>
      <div className="field">
        <label>Notes</label>
        <textarea className="input" value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={2} />
      </div>
    </Modal>
  );
}

function InvDateField({ label, value, field, invId, isAdmin, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const toast = useToast();

  function startEdit() {
    const d = value ? new Date(value) : new Date();
    setVal(d.toISOString().slice(0, 10));
    setEditing(true);
  }

  async function save() {
    try {
      await api.updateInvoice(invId, { [field]: val + 'T00:00:00.000Z' });
      onSave();
      setEditing(false);
    } catch(e) { toast(e.message, 'error'); }
  }

  if (!isAdmin) return value ? <span>{label}: {formatDate(value)}</span> : null;

  return editing ? (
    <div className="flex items-center gap-6">
      <span style={{ fontSize: 11 }}>{label}:</span>
      <input type="date" value={val} onChange={e => setVal(e.target.value)}
        style={{ background: 'var(--bg3)', border: '1px solid var(--primary)', borderRadius: 4, color: 'var(--text)', padding: '1px 4px', fontSize: 11 }} />
      <button onClick={save} style={{ color: 'var(--green)', cursor: 'pointer', background: 'none', border: 'none', fontSize: 13 }}>✓</button>
      <button onClick={() => setEditing(false)} style={{ color: 'var(--text3)', cursor: 'pointer', background: 'none', border: 'none', fontSize: 13 }}>✕</button>
    </div>
  ) : (
    <span style={{ cursor: 'pointer' }} onClick={startEdit}>
      {label}: {value ? formatDate(value) : <span style={{ color: 'var(--text3)' }}>—</span>} <span style={{ fontSize: 10, color: 'var(--primary)' }}>✏️</span>
    </span>
  );
}

function DateEditor({ label, value, onSave, isAdmin }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  function startEdit() {
    // Convert stored date to YYYY-MM-DD for input
    const d = value ? new Date(value) : new Date();
    setVal(d.toISOString().slice(0, 10));
    setEditing(true);
  }

  async function save() {
    await onSave(val + 'T00:00:00.000Z');
    setEditing(false);
  }

  if (!isAdmin) return (
    <div className="detail-meta-item">
      <div className="key">{label}</div>
      <div className="val">{value ? formatDate(value) : '—'}</div>
    </div>
  );

  return (
    <div className="detail-meta-item">
      <div className="key">{label}</div>
      {editing ? (
        <div className="flex items-center gap-8" style={{ marginTop: 2 }}>
          <input type="date" value={val} onChange={e => setVal(e.target.value)}
            style={{ background: 'var(--bg3)', border: '1px solid var(--primary)', borderRadius: 4, color: 'var(--text)', padding: '2px 6px', fontSize: 12 }} />
          <button className="btn btn-primary btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={save}>✓</button>
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setEditing(false)}>✕</button>
        </div>
      ) : (
        <div className="val flex items-center gap-8" style={{ cursor: 'pointer' }} onClick={startEdit}>
          {value ? formatDate(value) : <span className="text-dim">—</span>}
          <span style={{ fontSize: 11, color: 'var(--primary)' }}>✏️</span>
        </div>
      )}
    </div>
  );
}

function FaultEditor({ ticket, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(ticket.fault_description || '');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save() {
    setSaving(true);
    try { await onSave(val); setEditing(false); toast('Fault updated'); }
    catch(e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  if (editing) return (
    <div className="flex items-center gap-8 mt-8" style={{ flexWrap: 'wrap' }}>
      <input className="input" value={val} onChange={e => setVal(e.target.value)}
        style={{ flex: 1, minWidth: 200 }} autoFocus
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '...' : 'Save'}</button>
      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
    </div>
  );

  return (
    <p className="text-sm text-muted mt-8" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      Fault: {ticket.fault_description || <span className="text-dim">—</span>}
      <span onClick={() => { setVal(ticket.fault_description || ''); setEditing(true); }}
        style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 12 }}>✏️</span>
    </p>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [ticket, setTicket] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const isAdmin = useIsAdmin();
  const [deliverModal, setDeliverModal] = useState(false);
  const [deliverDate, setDeliverDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoicePopup, setInvoicePopup] = useState(null);
  const [notesEdit, setNotesEdit] = useState(false);
  const [notesVal, setNotesVal] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(() => {
    api.getTicket(id).then(setTicket).catch(() => navigate('/tickets'));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(status, delivered_at) {
    setUpdatingStatus(true);
    try {
      await api.updateTicket(id, { status, ...(delivered_at ? { delivered_at } : {}) });
      toast(`Status → ${STATUS_LABELS[status]}`);
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally { setUpdatingStatus(false); }
  }

  async function updateInvoiceStatus(invId, status) {
    await api.updateInvoice(invId, { status });
    toast(`Invoice → ${INVOICE_STATUS_LABELS[status]}`);
    load();
  }

  async function deletePart(partId) {
    await api.deletePart(partId);
    toast('Part removed');
    load();
  }

  async function updatePart(partId, data) {
    await api.updatePart(partId, data);
    load();
  }

  async function deleteInvoice(invId) {
    await api.deleteInvoice(invId);
    toast('Invoice removed');
    setConfirm(null);
    load();
  }

  async function deleteTicket() {
    await api.deleteTicket(id);
    toast('Ticket deleted');
    navigate('/tickets');
  }

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await api.updateTicket(id, { notes: notesVal });
      toast('Notes saved');
      setNotesEdit(false);
      load();
    } catch(e) { toast(e.message, 'error'); }
    finally { setSavingNotes(false); }
  }

  if (!ticket) return <div className="page"><div className="empty">Loading...</div></div>;

  const NEXT_STATUS = { received: 'in_repair', in_repair: 'done', done: 'delivered' };
  const PREV_STATUS = { in_repair: 'received', done: 'in_repair', delivered: 'done' };
  const nextStatus = NEXT_STATUS[ticket.status];
  const prevStatus = PREV_STATUS[ticket.status];

  function handleNextStatus() {
    if (nextStatus === 'delivered') {
      setDeliverDate(new Date().toISOString().slice(0, 10));
      setDeliverModal(true);
    } else {
      updateStatus(nextStatus);
    }
  }

  async function confirmDeliver() {
    await updateStatus('delivered', deliverDate + 'T00:00:00.000Z');
    setDeliverModal(false);
  }

  return (
    <div className="page">
      <span className="back-btn" onClick={() => navigate('/tickets')}>← Tickets</span>
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ type: 'ticket' })}>🗑 Delete Ticket</button>
        </div>
      )}

      <div className="detail-header">
        <div className="flex items-center gap-12" style={{ flexWrap: 'wrap' }}>
          <h1 className="page-title" style={{ fontSize: 16 }}>
            <span className="text-dim text-sm">TICKET</span> #{ticket.id.slice(0, 8).toUpperCase()}
          </h1>
          <StatusBadge status={ticket.status} />
          {ticket.parent_ticket_id && <span className="badge badge-red">↩ Return Ticket</span>}
          <div className="flex gap-8 ml-auto">
            {prevStatus && isAdmin && (
              <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(prevStatus)} disabled={updatingStatus}>
                ← {STATUS_LABELS[prevStatus]}
              </button>
            )}
            {nextStatus && isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={handleNextStatus} disabled={updatingStatus}>
                → {STATUS_LABELS[nextStatus]}
              </button>
            )}
            {ticket.status === 'delivered' && isAdmin && (
              <button className="btn btn-sm" style={{ background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}
                onClick={() => setModal('return')}>
                ↩ Return
              </button>
            )}
          </div>
        </div>

        <div className="detail-meta mt-8">
          <div className="detail-meta-item">
            <div className="key">Device</div>
            <div className="val link" onClick={() => navigate(`/devices/${ticket.device_id}`)}>{ticket.device_name}</div>
          </div>
          <div className="detail-meta-item">
            <div className="key">Serial</div>
            <div className="val">{ticket.device_serial}</div>
          </div>
          {ticket.device_alias && <div className="detail-meta-item">
            <div className="key">Alias</div>
            <div className="val">{ticket.device_alias}</div>
          </div>}
          {ticket.client_name && <div className="detail-meta-item">
            <div className="key">Client</div>
            <div className="val text-primary fw-600">{ticket.client_name}</div>
          </div>}
          <DateEditor label="Received" value={ticket.received_at} isAdmin={isAdmin}
            onSave={async (val) => { await api.updateTicket(id, { received_at: val }); load(); }} />
          {ticket.status === 'delivered' && (
            <DateEditor label="Delivered" value={ticket.delivered_at} isAdmin={isAdmin}
              onSave={async (val) => { await api.updateTicket(id, { delivered_at: val }); load(); }} />
          )}
          {ticket.status !== 'delivered' && (
            <div className="detail-meta-item">
              <div className="key">Status Since</div>
              <div className="val">{formatDate(ticket.updated_at)}</div>
            </div>
          )}
        </div>

        {isAdmin ? (
          <FaultEditor ticket={ticket} onSave={async (val) => { await api.updateTicket(id, { fault_description: val }); load(); }} />
        ) : (
          ticket.fault_description && <p className="text-sm text-muted mt-8">Fault: {ticket.fault_description}</p>
        )}
      </div>

      {/* Parent ticket */}
      {ticket.parent && (
        <div className="card mb-16" style={{ borderColor: 'var(--red-dim)' }}>
          <div className="text-xs text-dim mb-4" style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>↩ Returned from</div>
          <span className="link" onClick={() => navigate(`/tickets/${ticket.parent.id}`)}>
            #{ticket.parent.id.slice(0, 8)} — {ticket.parent.device_name} — {ticket.parent.fault_description || 'No description'}
          </span>
        </div>
      )}

      {/* Linked tickets */}
      {ticket.linked?.length > 0 && (
        <div className="card mb-16">
          <div className="text-xs text-dim mb-8" style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Linked Tickets</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ticket.linked.map(lt => (
              <div key={lt.id} className="flex items-center gap-8">
                <StatusBadge status={lt.status} />
                <span className="link" onClick={() => navigate(`/tickets/${lt.id}`)}>#{lt.id.slice(0, 8)}</span>
                <span className="text-sm text-muted">{lt.device_name}</span>
                <span className="text-sm text-dim">{formatDate(lt.received_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parts */}
      <div className="flex items-center gap-8 mb-8">
        <div className="section-title" style={{ flex: 1 }}>Parts Used</div>
        {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setModal('part')}>+ Add Part</button>}
      </div>
      <div className="table-wrap mb-20">
        <table>
          <thead>
            <tr>
              <th>Part</th>
              <th>Qty</th>
              <th>Warranty</th>
              <th>Status</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(!ticket.parts || ticket.parts.length === 0) ? (
              <tr><td colSpan={6}><div className="empty">No parts added</div></td></tr>
            ) : ticket.parts.map(p => (
              <PartRow key={p.id} part={p} onUpdate={updatePart} onDelete={deletePart} isAdmin={isAdmin}
                invoices={ticket.invoices} onInvoiceClick={setInvoicePopup} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Invoices */}
      <div className="flex items-center gap-8 mb-8">
        <div className="section-title" style={{ flex: 1 }}>Invoices</div>
        {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setModal('invoice')}>+ Track Invoice</button>}
      </div>

      {(!ticket.invoices || ticket.invoices.length === 0) ? (
        <div className="card"><div className="empty" style={{ padding: 24 }}>No invoices tracked</div></div>
      ) : ticket.invoices.map(inv => (
        <div key={inv.id} className="card mb-12">
          {/* Invoice header */}
          <div className="flex items-center gap-12" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <span className="mono fw-600" style={{ fontSize: 14 }}>{inv.invoice_number || 'No #'}</span>
              {inv.amount && <span className="mono text-primary fw-600" style={{ marginLeft: 10 }}>{inv.amount.toLocaleString()} EGP</span>}
            </div>
            <InvoiceBadge status={inv.status} />
            <div className="flex gap-12 text-sm text-dim" style={{ marginLeft: 'auto', flexWrap: 'wrap' }}>
              <InvDateField label="Issued" value={inv.issued_at} field="issued_at" invId={inv.id} isAdmin={isAdmin} onSave={load} />
              {(inv.sent_at || inv.status === 'sent' || inv.status === 'paid') && <InvDateField label="Sent" value={inv.sent_at} field="sent_at" invId={inv.id} isAdmin={isAdmin} onSave={load} />}
              {(inv.paid_at || inv.status === 'paid') && <InvDateField label="Paid" value={inv.paid_at} field="paid_at" invId={inv.id} isAdmin={isAdmin} onSave={load} />}
            </div>
          </div>

          {/* Linked parts */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Parts in this invoice
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {inv.linked_parts?.map(p => (
                <div key={p.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'var(--primary-dim)', border: '1px solid rgba(45,156,219,0.2)',
                  borderRadius: 20, padding: '3px 10px', fontSize: 12
                }}>
                  <span style={{ color: 'var(--primary)', fontWeight: 500 }}>{p.name}</span>
                  {isAdmin && (
                    <span style={{ color: 'var(--text3)', cursor: 'pointer', fontSize: 11 }}
                      onClick={() => api.unlinkPartFromInvoice(inv.id, p.id).then(load)}>✕</span>
                  )}
                </div>
              ))}
              {isAdmin && ticket.parts?.filter(p => !inv.linked_parts?.find(lp => lp.id === p.id)).length > 0 && (
                <select
                  style={{
                    background: 'var(--bg3)', border: '1px dashed var(--border2)', borderRadius: 20,
                    padding: '3px 10px', fontSize: 12, color: 'var(--text3)', cursor: 'pointer', outline: 'none'
                  }}
                  value=""
                  onChange={e => {
                    if (e.target.value) api.linkPartToInvoice(inv.id, e.target.value).then(load);
                  }}
                >
                  <option value="">+ Link part...</option>
                  {ticket.parts
                    .filter(p => !inv.linked_parts?.find(lp => lp.id === p.id))
                    .map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                  }
                </select>
              )}
              {(!inv.linked_parts || inv.linked_parts.length === 0) && !isAdmin && (
                <span className="text-dim text-sm">No parts linked</span>
              )}
            </div>
          </div>

          {/* Actions */}
          {isAdmin && (
            <div className="flex gap-8" style={{ flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              {inv.status === 'paid' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoiceStatus(inv.id, 'sent')}>← Sent</button>}
              {inv.status === 'sent' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoiceStatus(inv.id, 'issued')}>← Issued</button>}
              {inv.status === 'issued' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoiceStatus(inv.id, 'sent')}>→ Sent</button>}
              {inv.status === 'sent' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoiceStatus(inv.id, 'paid')}>→ Paid</button>}
              {inv.status === 'issued' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoiceStatus(inv.id, 'paid')}>→ Paid</button>}
              <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ type: 'invoice', id: inv.id })}>Delete</button>
            </div>
          )}
        </div>
      ))}

      {/* Engineer Notes */}
      <TicketFinance ticketId={id} refreshKey={(ticket.invoices || []).map(i => `${i.id}:${i.status}:${i.amount}`).join('|')} />

      <div className="flex items-center gap-8 mb-8" style={{ marginTop: 8 }}>
        <div className="section-title" style={{ flex: 1 }}>Engineer Notes</div>
        {isAdmin && !notesEdit && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setNotesVal(ticket.notes || ''); setNotesEdit(true); }}>
            ✏️ {ticket.notes ? 'Edit' : 'Add Notes'}
          </button>
        )}
      </div>
      <div className="card" style={{ marginBottom: 20 }}>
        {notesEdit ? (
          <div>
            <textarea className="input" value={notesVal}
              onChange={e => setNotesVal(e.target.value)}
              rows={5} placeholder="Write engineer notes here..." style={{ marginBottom: 12 }} />
            <div className="flex gap-8">
              <button className="btn btn-primary btn-sm" onClick={saveNotes} disabled={savingNotes}>
                {savingNotes ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setNotesEdit(false)}>Cancel</button>
            </div>
          </div>
        ) : ticket.notes ? (
          <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{ticket.notes}</p>
        ) : (
          <p className="text-dim text-sm" style={{ fontStyle: 'italic' }}>No engineer notes yet.</p>
        )}
      </div>

      {modal === 'part' && <AddPartModal ticketId={id} onSave={load} onClose={() => setModal(null)} />}
      {modal === 'invoice' && <AddInvoiceModal ticketId={id} onSave={load} onClose={() => setModal(null)} />}
      {modal === 'return' && <ReturnModal ticket={ticket} onClose={() => setModal(null)} />}
      {invoicePopup && <InvoiceDetailModal inv={invoicePopup} onClose={() => setInvoicePopup(null)} />}

      {deliverModal && (
        <Modal title="Confirm Delivery Date" onClose={() => setDeliverModal(false)} footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setDeliverModal(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={confirmDeliver}>✓ Mark as Delivered</button>
          </>
        }>
          <div className="field">
            <label>Delivery Date</label>
            <input type="date" className="input" value={deliverDate}
              onChange={e => setDeliverDate(e.target.value)} />
          </div>
          <p className="text-sm text-muted mt-8">Warranty on parts will start from this date.</p>
        </Modal>
      )}
      {confirm && (
        <ConfirmModal
          message={confirm.type === 'ticket' ? 'Delete this ticket? All parts and invoices will be deleted.' : `Delete this ${confirm.type}?`}
          onConfirm={() => {
            if (confirm.type === 'invoice') deleteInvoice(confirm.id);
            if (confirm.type === 'ticket') deleteTicket();
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
